import { useState, useEffect, useCallback, useRef } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import {
  ServiceRequest,
  Patient,
  Practitioner,
  Specimen,
  Consent,
  DiagnosticReport,
} from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { OrderFormData } from '../components/OrderForm';
import {
  extractFormDataFromServiceRequest,
  sortDiagnosticReportsByIssuedDate,
} from '../utils/fhir-helpers';

export interface OrderDetailsData {
  serviceRequest: ServiceRequest | null;
  patient: Patient | undefined;
  requester: Practitioner | undefined;
  specimens: Specimen[];
  consent: Consent | undefined;
  diagnosticReports: DiagnosticReport[];
}

export function useOrderDetails(orderId?: string) {
  const medplum = useMedplum();
  const { t } = useTranslation('lims');
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [orderData, setOrderData] = useState<OrderDetailsData>({
    serviceRequest: null,
    patient: undefined,
    requester: undefined,
    specimens: [],
    consent: undefined,
    diagnosticReports: [],
  });

  const loadOrderDetails = useCallback(async () => {
    if (!orderId) {
      return;
    }

    // Prevent concurrent calls
    if (loadingRef.current) {
      return;
    }
    setLoading(true);
    loadingRef.current = true;
    try {
      // Invalidate cache so executeBatch mutations are reflected
      medplum.invalidateAll();

      // Single bundle search: fetch ServiceRequest with all related resources
      const bundle = await medplum.search('ServiceRequest', [
        ['_id', orderId],
        ['_include', 'ServiceRequest:patient'],
        ['_include', 'ServiceRequest:requester'],
        ['_include', 'ServiceRequest:specimen'],
        ['_revinclude', 'Consent:data'],
        ['_revinclude', 'DiagnosticReport:based-on'],
      ]);

      // Extract the main ServiceRequest
      const serviceRequest = bundle.entry?.find(
        (e) =>
          e.search?.mode === 'match' &&
          e.resource?.resourceType === 'ServiceRequest'
      )?.resource as ServiceRequest | undefined;

      if (!serviceRequest) {
        setOrderData({
          serviceRequest: null,
          patient: undefined,
          requester: undefined,
          specimens: [],
          consent: undefined,
          diagnosticReports: [],
        });
        return;
      }

      // Extract included/revincluded resources from bundle
      let patient: Patient | undefined;
      let requester: Practitioner | undefined;
      const specimens: Specimen[] = [];
      let consent: Consent | undefined;
      const diagnosticReports: DiagnosticReport[] = [];

      for (const entry of bundle.entry ?? []) {
        if (entry.search?.mode !== 'include') {
          continue;
        }
        const resource = entry.resource as any;
        if (!resource) {
          continue;
        }

        switch (resource.resourceType) {
          case 'Patient':
            patient = resource;
            break;
          case 'Practitioner':
            requester = resource;
            break;
          case 'Specimen':
            specimens.push(resource);
            break;
          case 'Consent':
            if (!consent) {
              consent = resource;
            }
            break;
          case 'DiagnosticReport':
            diagnosticReports.push(resource);
            break;
        }
      }

      // Sort diagnostic reports by issued date (most recent first)
      sortDiagnosticReportsByIssuedDate(diagnosticReports);

      setOrderData({
        serviceRequest,
        patient,
        requester,
        specimens,
        consent,
        diagnosticReports,
      });
    } catch (error: any) {
      notifications.show({
        title: t('notifications.order.loadError'),
        message: error.message || t('notifications.order.loadErrorMessage'),
        color: 'red',
      });
      throw error;
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [orderId, medplum, t]);

  const getFormDataFromOrder = useCallback((): Partial<OrderFormData> => {
    if (!orderData.serviceRequest) {
      return {};
    }

    const extractedData = extractFormDataFromServiceRequest(
      orderData.serviceRequest
    );
    return {
      ...extractedData,
      patient: orderData.patient,
      requester: orderData.requester,
    };
  }, [orderData.serviceRequest, orderData.patient, orderData.requester]);

  useEffect(() => {
    loadOrderDetails();
  }, [loadOrderDetails]);

  //Subscribe to ServiceRequest updates
  useSubscription(orderId ? `ServiceRequest/${orderId}` : '', (bundle) => {
    const updatedServiceRequest = bundle.entry?.find(
      (sr) => sr.resource?.resourceType === 'ServiceRequest'
    )?.resource as ServiceRequest | undefined;

    if (!updatedServiceRequest) {
      return;
    }

    // Update the service request in state
    setOrderData((prev) => ({
      ...prev,
      serviceRequest: updatedServiceRequest,
    }));
  });

  //Subscribe to Specimen changes for this order
  useSubscription(
    orderId ? `Specimen?request=ServiceRequest/${orderId}` : '',
    (bundle) => {
      const updatedSpecimen = bundle.entry?.find(
        (s) => s.resource?.resourceType === 'Specimen'
      )?.resource as Specimen | undefined;

      if (!updatedSpecimen) {
        return;
      }

      //Update specimens list
      setOrderData((prev) => {
        const exists = prev.specimens.find((s) => s.id === updatedSpecimen.id);

        if (exists) {
          //Update existing specimen
          return {
            ...prev,
            specimens: prev.specimens.map((s) =>
              s.id === updatedSpecimen.id ? updatedSpecimen : s
            ),
          };
        } else {
          // Add new specimen
          return {
            ...prev,
            specimens: [...prev.specimens, updatedSpecimen],
          };
        }
      });
    }
  );

  // Subscribe to DiagnosticReport changes for this order
  useSubscription(
    orderId ? `DiagnosticReport?based-on=ServiceRequest/${orderId}` : '',
    (bundle) => {
      const updatedReport = bundle.entry?.find(
        (dr) => dr.resource?.resourceType === 'DiagnosticReport'
      )?.resource as DiagnosticReport | undefined;

      if (!updatedReport) {
        return;
      }

      setOrderData((prev) => {
        const exists = prev.diagnosticReports.find(
          (dr) => dr.id === updatedReport.id
        );

        if (exists) {
          return {
            ...prev,
            diagnosticReports: prev.diagnosticReports.map((dr) =>
              dr.id === updatedReport.id ? updatedReport : dr
            ),
          };
        } else {
          const updated = [updatedReport, ...prev.diagnosticReports];
          return {
            ...prev,
            diagnosticReports: sortDiagnosticReportsByIssuedDate(updated),
          };
        }
      });
    }
  );

  const updateServiceRequest = useCallback((sr: ServiceRequest) => {
    setOrderData((prev) => ({ ...prev, serviceRequest: sr }));
  }, []);

  return {
    ...orderData,
    loading,
    loadOrderDetails,
    updateServiceRequest,
    getFormDataFromOrder,
  };
}
